import { format } from "date-fns";
import { Plus, Pencil, Trash2, MoreVertical, FolderInput, MoveRight, ChevronDown, ChevronRight } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";

import { MomentumIndicator } from "@/components/dashboard/MomentumIndicator";
import { MemoryManager } from "@/components/memories/MemoryManager";
import { AppBreadcrumb } from "@/components/navigation/AppBreadcrumb";
import { MomentumSummary } from "@/components/projects/MomentumSummary";
import { ProjectActivityView } from "@/components/projects/ProjectActivityView";
import { ProjectTags } from "@/components/projects/ProjectTags";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useProjectDetailState } from "@/hooks/useProjectDetailState";
import { useProjectMomentum } from "@/hooks/useProjectMomentum";

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { momentum } = useProjectMomentum(projectId);
  
  const {
    state,
    dispatch,
    project,
    sessions,
    projectTags,
    availableTags,
    folders,
    availableProjects,
    loading,
    createSession,
    isCreatingSession,
    handleEditName,
    handleSaveName,
    handleEditDescription,
    handleSaveDescription,
    isSaving,
    handleDeleteProject,
    isDeleting,
    handleArchiveProject,
    handleEditSession,
    handleSaveSessionName,
    isSavingSession,
    handleDeleteSession,
    isDeletingSession,
    handleMoveToFolder,
    handleMoveSessions,
    isMovingSessions,
    fetchProjectData,
  } = useProjectDetailState(projectId);

  const {
    selectedFolderId,
    selectedSessionIds,
    targetProjectId,
    activityExpanded,
    editNameDialog,
    editDescDialog,
    deleteDialog,
    editSessionDialog,
    deleteSessionDialog,
    folderDialog,
    moveSessionDialog,
    editedName,
    editedDescription,
    editedSessionName,
    selectedSession,
  } = state;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppBreadcrumb />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-semibold">{project?.name}</h1>
                {momentum && (
                  <MomentumIndicator
                    score={momentum.momentum_score}
                    level={momentum.momentum_level}
                    size="lg"
                  />
                )}
              </div>
              {project?.description && (
                <p className="text-muted-foreground mb-2">{project.description}</p>
              )}
              <div className="mt-3 space-y-2">
                <ProjectTags
                  projectId={projectId!}
                  projectTags={projectTags}
                  availableTags={availableTags}
                  onRefresh={fetchProjectData}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">All sessions for this project</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleEditName}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit name
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEditDescription}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit description
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => dispatch({ type: 'OPEN_FOLDER_DIALOG' })}>
                  <FolderInput className="h-4 w-4 mr-2" />
                  Move to folder
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleArchiveProject}>
                  {project?.archived ? "📂" : "📁"} 
                  <span className="ml-2">{project?.archived ? 'Unarchive' : 'Archive'} project</span>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => dispatch({ type: 'OPEN_DELETE_DIALOG' })}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-semibold">Sessions</h2>
            {selectedSessionIds.size > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => dispatch({ type: 'OPEN_MOVE_SESSION_DIALOG' })}
              >
                <MoveRight className="h-4 w-4 mr-2" />
                Move {selectedSessionIds.size} session(s)
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <MemoryManager
              projectId={projectId}
              scopeLabel={project?.name || "this project"}
            />
            <Button onClick={() => createSession()} disabled={isCreatingSession}>
              <Plus className="h-4 w-4 mr-2" />
              {isCreatingSession ? 'Creating...' : 'New Session'}
            </Button>
          </div>
        </div>

        {sessions.length === 0 ? (
          <Card>
            <CardHeader className="text-center py-12">
              <p className="text-muted-foreground mb-4">No sessions yet</p>
              <Button onClick={() => createSession()} disabled={isCreatingSession}>
                <Plus className="h-4 w-4 mr-2" />
                {isCreatingSession ? 'Creating...' : 'Start your first session'}
              </Button>
            </CardHeader>
          </Card>
        ) : (
          <>
            {sessions.length > 1 && (
              <div className="mb-4 flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectedSessionIds.size === sessions.length}
                  onCheckedChange={() => {
                    const allSelected = selectedSessionIds.size === sessions.length;
                    if (allSelected) {
                      dispatch({ type: 'CLEAR_SESSION_SELECTION' });
                    } else {
                      sessions.forEach(s => dispatch({ type: 'TOGGLE_SESSION_SELECTION', payload: s.id }));
                    }
                  }}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select all sessions
                </Label>
              </div>
            )}
            <div className="space-y-3">
              {sessions.map((session) => (
                <Card
                  key={session.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardHeader className="flex flex-row items-start justify-between space-y-0">
                    <div className="flex items-start gap-3 flex-1">
                      <Checkbox
                        id={`session-${session.id}`}
                        checked={selectedSessionIds.has(session.id)}
                        onCheckedChange={() => dispatch({ type: 'TOGGLE_SESSION_SELECTION', payload: session.id })}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                      />
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/sessions/${session.id}`)}
                      >
                        <CardTitle className="text-lg font-medium mb-2">{session.name}</CardTitle>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Created: {format(new Date(session.created_at), "PPp")}</p>
                          <p>Last updated: {format(new Date(session.updated_at), "PPp")}</p>
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          handleEditSession(session);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Rename session
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch({ type: 'OPEN_DELETE_SESSION_DIALOG', payload: session });
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete session
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Momentum Summary Section */}
        {project && sessions.length > 0 && (
          <div className="mt-8">
            <MomentumSummary projectId={project.id} />
          </div>
        )}

        {/* Project Activity Section */}
        {project && (
          <div className="mt-12">
            <Collapsible open={activityExpanded} onOpenChange={() => dispatch({ type: 'TOGGLE_ACTIVITY_EXPANDED' })}>
              <Card>
                <CardHeader className="pb-3">
                  <CollapsibleTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="w-full justify-between hover:bg-muted/50 -mx-2 px-2"
                    >
                      <CardTitle className="text-xl">Activity Timeline</CardTitle>
                      {activityExpanded ? (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                </CardHeader>
                <CollapsibleContent>
                  <ProjectActivityView 
                    projectId={project.id} 
                    projectName={project.name} 
                  />
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        )}

        {/* Edit Name Dialog */}
        <Dialog open={editNameDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_EDIT_NAME_DIALOG' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Project Name</DialogTitle>
              <DialogDescription>
                Update the name of your project
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-name">Project Name</Label>
                <Input
                  id="project-name"
                  value={editedName}
                  onChange={(e) => dispatch({ type: 'SET_EDITED_NAME', payload: e.target.value })}
                  placeholder="Enter project name"
                  maxLength={100}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'CLOSE_EDIT_NAME_DIALOG' })}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveName} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Description Dialog */}
        <Dialog open={editDescDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_EDIT_DESC_DIALOG' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Project Description</DialogTitle>
              <DialogDescription>
                Add or update the description of your project
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-description">Description</Label>
                <Textarea
                  id="project-description"
                  value={editedDescription}
                  onChange={(e) => dispatch({ type: 'SET_EDITED_DESCRIPTION', payload: e.target.value })}
                  placeholder="Enter project description (optional)"
                  rows={4}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {editedDescription.length}/500 characters
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'CLOSE_EDIT_DESC_DIALOG' })}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveDescription} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Project Confirmation Dialog */}
        <AlertDialog open={deleteDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_DELETE_DIALOG' })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the project "{project?.name}" and all of its sessions.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteProject()}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? "Deleting..." : "Delete Project"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Session Name Dialog */}
        <Dialog open={editSessionDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_EDIT_SESSION_DIALOG' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Rename Session</DialogTitle>
              <DialogDescription>
                Update the name of this session
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="session-name">Session Name</Label>
                <Input
                  id="session-name"
                  value={editedSessionName}
                  onChange={(e) => dispatch({ type: 'SET_EDITED_SESSION_NAME', payload: e.target.value })}
                  placeholder="Enter session name"
                  maxLength={100}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'CLOSE_EDIT_SESSION_DIALOG' })}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveSessionName} disabled={isSavingSession}>
                {isSavingSession ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Session Confirmation Dialog */}
        <AlertDialog open={deleteSessionDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_DELETE_SESSION_DIALOG' })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Session?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete the session "{selectedSession?.name}" and all of its messages.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingSession}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSession}
                disabled={isDeletingSession}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeletingSession ? "Deleting..." : "Delete Session"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Move to Folder Dialog */}
        <Dialog open={folderDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_FOLDER_DIALOG' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Move to Folder</DialogTitle>
              <DialogDescription>
                Select a folder for this project
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="folder-select">Folder</Label>
                <Select
                  value={selectedFolderId || "root"}
                  onValueChange={(value) => dispatch({ type: 'SET_SELECTED_FOLDER_ID', payload: value === "root" ? null : value })}
                >
                  <SelectTrigger id="folder-select">
                    <SelectValue placeholder="Select a folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">Root (No Folder)</SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'CLOSE_FOLDER_DIALOG' })}
              >
                Cancel
              </Button>
              <Button onClick={handleMoveToFolder}>
                Move
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Move Sessions Dialog */}
        <Dialog open={moveSessionDialog} onOpenChange={(open) => !open && dispatch({ type: 'CLOSE_MOVE_SESSION_DIALOG' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Move Sessions to Project</DialogTitle>
              <DialogDescription>
                Select the project to move {selectedSessionIds.size} session(s) to
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="project-select">Target Project</Label>
                <Select value={targetProjectId} onValueChange={(value) => dispatch({ type: 'SET_TARGET_PROJECT_ID', payload: value })}>
                  <SelectTrigger id="project-select">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProjects.map((proj) => (
                      <SelectItem key={proj.id} value={proj.id}>
                        {proj.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => dispatch({ type: 'CLOSE_MOVE_SESSION_DIALOG' })}
                disabled={isMovingSessions}
              >
                Cancel
              </Button>
              <Button onClick={handleMoveSessions} disabled={isMovingSessions || !targetProjectId}>
                {isMovingSessions ? "Moving..." : "Move Sessions"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ProjectDetail;
